/**
 * A dialog of our own, and the end of `window.prompt`.
 *
 * The browser's three — `alert`, `confirm`, `prompt` — are the one part of this
 * area that was never designed: a grey box at the top of the screen, in the
 * browser's own typeface, with the origin printed above it and two buttons
 * whose words nobody chose. They also block the page entirely, which is why a
 * name typed into one could not be validated, could not say what it would do,
 * and could not be cancelled with Escape into a page that had carried on.
 *
 * What is here instead is the smallest dialog that can do the same three jobs:
 *
 * - **Ask for a word.** One field, its own label, and a verb that says what the
 *   word is for — `Create the team`, not `OK`.
 * - **Ask for a decision.** The same panel with no field, and a verb that names
 *   the act. A destructive one is red and still says what it does.
 * - **Say why it could not.** The refusal lands *in* the dialog rather than
 *   replacing it, so what was typed is still there to correct.
 *
 * Three behaviours are the whole of the interaction, and they are the ones a
 * person already expects: Escape closes it, Enter commits it, and the field is
 * focused the moment it opens with its value selected — a rename that made
 * somebody clear the old name by hand was a rename nobody used.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { gerund } from '../lib/format';
import { useFocusTrap } from '../lib/focus-trap';
import { useMotionMount } from '../lib/presence';
import { color, font, radius } from '../theme/tokens';
import { GhostButton, Input, PrimaryButton } from './primitives';

export function Modal({
  title,
  /** The sentence under the title: what this will do, in the words of the act. */
  lede,
  /** The label over the field. With none, the dialog asks for a decision. */
  label,
  value,
  placeholder,
  onChange,
  /** The verb that commits. Never “OK”: it says what happens. */
  confirm,
  /** Whether that verb is the destructive one. */
  danger,
  busy,
  problem,
  whenEmpty,
  held,
  /** What the title's own line says on the right: a version, a date, a count. */
  meta,
  /**
   * The sentence beside the verbs, where pressing one deserves one.
   *
   * It is the way *out* rather than a warning — "v14 stays restorable from the
   * history" is what makes a publish pressable — so it sits with the buttons
   * and not above the content, where it would read as a caveat on what is being
   * shown.
   */
  foot,
  /** Wider than the ask, for a dialog that shows something rather than asks. */
  width,
  /** What goes and what stays (§19, rule 7): one line per thing, always both. */
  goes,
  stays,
  onConfirm,
  onCancel,
  children
}: {
  title: string;
  lede?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  confirm: string;
  danger?: boolean;
  busy?: boolean;
  problem?: string | null;
  /** Said under the field when the verb is pressed with it empty (« Give the team a name. »). */
  whenEmpty?: string;
  /** Why the verb is held (rule 6) — « Type a new name first » while the name is unchanged; it stays focusable and says so. */
  held?: string;
  meta?: React.ReactNode;
  foot?: React.ReactNode;
  width?: number;
  goes?: readonly string[];
  stays?: readonly string[];
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}): React.JSX.Element {
  const field = useRef<HTMLInputElement>(null);
  // Entrance (§35): the veil fades in, the panel from o 0, s .96, y 8, d 1 r .35;
  // on close the whole fades out in 120 ms.
  const veil = useMotionMount<HTMLDivElement>({ o: 0 }, { d: 1, r: 0.35 }, { o: 0 });
  const panel = useMotionMount<HTMLDivElement>({ o: 0, s: 0.96, y: 8 }, { d: 1, r: 0.35 });

  // Escape closes it wherever the focus is, including on the overlay itself —
  // a dialog that can only be dismissed by finding its Cancel is a dialog that
  // gets dismissed by reloading the page.
  useEffect(() => {
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onCancel]);

  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);

  // Tab stays inside the dialog, and the focus goes back to what opened it on
  // close (rule 31) — a ⋯ menu's button, a row's verb.
  const box = useRef<HTMLDivElement | null>(null);
  const panelRef = useCallback(
    (node: HTMLDivElement | null): void => {
      box.current = node;
      panel(node);
    },
    [panel]
  );
  useFocusTrap(box);

  const asked = label !== undefined;
  const empty = asked && !value?.trim();
  const [triedEmpty, setTriedEmpty] = useState(false);

  return (
    <div
      ref={veil}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '12vh 20px 20px',
        background: 'rgba(22,25,31,.28)'
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          width: '100%',
          maxWidth: width ?? 460,
          padding: 22,
          border: `1px solid ${color.line}`,
          borderRadius: radius.xxl,
          background: color.surface,
          boxShadow: '0 1px 2px rgba(22,25,31,.06), 0 30px 60px -20px rgba(22,25,31,.32)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, color: color.ink }}>{title}</span>
          {meta && <span style={{ flex: 'none', fontFamily: font.mono, fontSize: 11.5, color: color.inkFaint }}>{meta}</span>}
        </div>
        {lede && (
          <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.6, color: color.inkSubtle }}>{lede}</p>
        )}
        {goes && <Consequences kicker="What goes" tint={color.badText} lines={goes} />}
        {stays && <Consequences kicker="What stays" tint={color.inkMuted} lines={stays} />}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (empty) setTriedEmpty(true);
            else if (!busy && !held) onConfirm();
          }}
        >
          {asked && (
            <label style={{ display: 'block', marginTop: 18 }}>
              <span style={{ display: 'block', fontSize: 12.5, color: color.inkSubtle, marginBottom: 6 }}>{label}</span>
              <Input
                ref={field}
                value={value ?? ''}
                placeholder={placeholder}
                onChange={(event) => onChange?.(event.target.value)}
              />
            </label>
          )}
          {children}
          {(problem || (triedEmpty && empty && whenEmpty)) && (
            <p style={{ margin: '12px 0 0', fontSize: 13, lineHeight: 1.5, color: color.badText }}>
              {problem || whenEmpty}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
            {foot && (
              <span style={{ flex: 1, minWidth: 0, fontSize: 12, lineHeight: 1.45, color: color.inkFaint }}>{foot}</span>
            )}
            <GhostButton size="sm" onClick={onCancel}>
              Cancel
            </GhostButton>
            {/* Ink, not teal. A dialog's verb commits what is already on
                screen — it is the chrome's rule, not the page's accent, and a
                green button here read as an offer rather than as the answer to
                the question above it. Red stays red: what cannot be undone is
                the one thing worth colouring differently. */}
            <PrimaryButton size="sm" type="submit" tone={danger ? 'danger' : 'ink'} reason={busy || empty ? undefined : held}>
              {busy ? gerund(confirm) : confirm}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

/** One half of « What goes / What stays »: its kicker, then a line per thing, full width. */
function Consequences({
  kicker,
  tint,
  lines
}: {
  kicker: string;
  tint: string;
  lines: readonly string[];
}): React.JSX.Element {
  return (
    <div style={{ marginTop: 14 }}>
      <div
        style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: tint }}
      >
        {kicker}
      </div>
      {lines.map((line) => (
        <div key={line} style={{ fontSize: 13, lineHeight: 1.5, color: color.inkBody, marginTop: 4 }}>
          {line}
        </div>
      ))}
    </div>
  );
}

/** What a decision asks, which is everything `Modal` needs minus its plumbing. */
export interface Ask {
  title: string;
  lede?: string;
  /** What goes and what stays, where a removal says both (§19). */
  goes?: readonly string[];
  stays?: readonly string[];
  /** The verb that commits. Never "OK": it says what happens. */
  confirm: string;
  danger?: boolean;
}

/** The same, for the questions that want a word back rather than a yes. */
export interface AskForWord extends Ask {
  /** The label over the field. Its presence is what makes this a prompt. */
  label: string;
  placeholder?: string;
  /** What the field starts with, selected, so a good default is one keystroke. */
  initial?: string;
}

type Pending =
  | { kind: 'decision'; ask: Ask; settle: (ok: boolean) => void }
  | { kind: 'word'; ask: AskForWord; settle: (word: string | null) => void };

/**
 * The browser's own dialogs, replaced where they stand.
 *
 * This file has said since it was written why `confirm` and `prompt` should not
 * be here: a grey box in the browser's typeface, with the origin printed above
 * it and two buttons whose words nobody chose. They also **block the
 * renderer** — not the page, the whole thread — so nothing behind them paints,
 * no keystroke is handled, and any automation driving the page stops dead until
 * a human clicks.
 *
 * Replacing them one at a time meant a piece of state, a handler split in two
 * and a `<Modal>` in the tree for every one, and a dozen chances to get it
 * subtly different. So the shape of the calls they replace is kept instead:
 *
 *     if (!(await ask({ title: 'Remove this machine?', confirm: 'Remove' }))) return;
 *     const why = await askFor({ title: 'Why?', label: 'Reason', confirm: 'Revoke' });
 *
 * read as `window.confirm(...)` and `window.prompt(...)` did, and the rest of
 * each handler is untouched. A decision settles `true` on the verb and `false`
 * on Cancel, Escape or a click outside; a word settles the string, or `null`
 * the same three ways — and the verb cannot be pressed while the field is
 * empty, so a word that comes back is never a blank one.
 *
 * Unmounting settles whatever is open the same as a cancel, rather than leaving
 * it hanging: a caller awaiting a dialog that has gone would otherwise never
 * run its next line, and never say why.
 */
export function useConfirm(): [
  React.JSX.Element | null,
  (ask: Ask) => Promise<boolean>,
  (ask: AskForWord) => Promise<string | null>
] {
  const [pending, setPending] = useState<Pending | null>(null);
  const [word, setWord] = useState('');
  // Bumped per question, and used as the dialog's key: a second one asked while
  // the first is open is a different dialog, and has to be mounted as one or it
  // opens with the last answer still in the field and the focus nowhere.
  const [nth, setNth] = useState(0);
  const cancel = useRef<(() => void) | null>(null);

  /**
   * The canceller is swapped **inside** here, and that ordering is the whole of
   * why this is one function rather than two lines at each call site.
   *
   * Written the other way round — the caller assigning `cancel.current` and
   * this then calling it — every question cancelled *itself* the instant it was
   * asked: the dialog appeared, the promise had already settled `false`, and
   * pressing the verb did nothing at all, because settling a settled promise is
   * a no-op. Every destructive action in the area silently stopped working
   * while still looking exactly right.
   */
  const open = useCallback((next: Pending, initial: string, canceller: () => void): void => {
    // One at a time. A second question asked while the first is up cancels the
    // first — the previous one, never the one being asked.
    const previous = cancel.current;
    cancel.current = canceller;
    previous?.();
    setWord(initial);
    setNth((n) => n + 1);
    setPending(next);
  }, []);

  const ask = useCallback(
    (next: Ask) =>
      new Promise<boolean>((resolve) => {
        open({ kind: 'decision', ask: next, settle: resolve }, '', () => resolve(false));
      }),
    [open]
  );

  const askFor = useCallback(
    (next: AskForWord) =>
      new Promise<string | null>((resolve) => {
        open({ kind: 'word', ask: next, settle: resolve }, next.initial ?? '', () => resolve(null));
      }),
    [open]
  );

  useEffect(
    () => () => {
      cancel.current?.();
      cancel.current = null;
    },
    []
  );

  const close = (commit: boolean): void => {
    if (!pending) return;
    cancel.current = null;
    setPending(null);
    if (pending.kind === 'word') pending.settle(commit ? word.trim() : null);
    else pending.settle(commit);
  };

  const dialog = pending ? (
    <Modal
      key={nth}
      title={pending.ask.title}
      lede={pending.ask.lede}
      goes={pending.ask.goes}
      stays={pending.ask.stays}
      confirm={pending.ask.confirm}
      danger={pending.ask.danger}
      {...(pending.kind === 'word'
        ? { label: pending.ask.label, placeholder: pending.ask.placeholder, value: word, onChange: setWord }
        : {})}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  ) : null;

  return [dialog, ask, askFor];
}
